import { useLocation, useNavigate } from 'react-router-dom';
import { logout, type SessionClaims } from '../api.js';
import { Brand } from './Brand.js';
import { isSection } from '../nav.js';
import { canCreateProject } from '../roles.js';

/**
 * THE HUB'S OWN CHROME — the staff application's header, and the thing that makes it read as a
 * different product from the Deal Analyzer rather than the same one at a different address.
 *
 * ## Why this exists at all
 *
 * The Hub had its own bundle, its own origin and its own tab title, and then rendered the ANALYZER's
 * header on every page. Two of that header's destinations do not exist here: `Deals` navigates to
 * `/analyses` and `+ New deal` to `/`, and the Hub's router sends both straight back to `/projects`.
 * A primary action in the header, on every page, that returns you to where you already were.
 *
 * Brad, 2026-08-26: _"The Hub looks a lot like the Deal Analyzer… they should be two separate
 * applications."_ Right on both counts, and the dead nav was the proof.
 *
 * ## What is shared and what is not
 *
 * **Shared: the brand.** Same GGPathOS mark, same navy/gold, same Montserrat/Inter, same components
 * underneath — Brad's instruction was _"everything should be GGP Branding but these are two separate
 * apps"_, and Chad's own PEE gate theme sets exactly the same tokens (navy `#0D3F59`, cream
 * `#FAF8F0`). Two products from one company should look related.
 *
 * **Not shared: the application identity.** The mark is followed by the app's NAME, so a staff member
 * can tell at a glance which of the two they are in — the one thing the Hub could not say about
 * itself, on any screen, despite the tab saying it.
 *
 * ## The nav is the Hub's verbs and nothing else
 *
 * ADR-0035 §7.3: the Hub is _"not a dashboard: a search box, a currency badge, and a drop zone."_ So
 * the nav is Projects and the scoring standard, both of which are real Hub routes.
 *
 * ⚠️ NO `+ New deal` and NO MCP token panel. Underwriting a deal is the ANALYZER's job and a record is
 * opened from the Projects page (`Open a record`), which is the canon-R-a door — project creation is
 * human-initiated through the gate. Offering a second, differently-worded creation verb in the header
 * would be a second door to a record whose birth is deliberately governed.
 *
 * ⚠️ And no link to the analyzer either, for now. Staff use both, so a cross-link is defensible — but
 * it has to open the OTHER ORIGIN in a new tab and say that it is leaving, which needs the analyzer's
 * URL in config (it is not there). A nav item that pretends the other app is a section of this one is
 * exactly what was just removed, so the wrong version of this is worse than its absence.
 */
export function HubHeader({ claims }: { claims: SessionClaims }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCurrent = (path: string): boolean => isSection(pathname, path);

  // Same permissive-claims read as the analyzer's header; the email is the label staff recognise.
  const email = claims['email'];
  const sub = claims['sub'];
  const label = typeof email === 'string' ? email : typeof sub === 'string' ? sub : null;

  async function onSignOut() {
    await logout();
    void navigate('/sign-in', { replace: true });
  }

  return (
    <header className="app__header hub__header">
      {/*
        The mark and the app name are ONE affordance and both go to the Hub's front door, which is the
        project list rather than `/`. The analyzer's header sends its logo to `/` — here that is a
        redirect, and a home button that bounces through a redirect is the smell this file removes.
      */}
      <button
        type="button"
        className="app__brandlink app__brandlink--named"
        onClick={() => void navigate('/projects')}
        aria-label="The Hub — GGPathOS home"
      >
        <Brand />
        {/* The application's NAME, in the chrome, on every page. `aria-hidden` because the button's
            own label already says it and a screen reader should not hear it twice. */}
        <span className="app__wordmark" aria-hidden="true">
          The Hub
        </span>
      </button>

      <div className="app__identity">
        <button
          type="button"
          className={`app__navlink${isCurrent('/projects') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/projects') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/projects')}
        >
          Projects
        </button>
        {/*
          THE TRACKING-LOG IMPORT. Here rather than on the projects list because that page is shared
          with the analyzer, and GGP's whole book of work is not something a client-facing bundle
          should carry a door to — this header exists only in the Hub, so the door is staff-only by
          construction rather than by a conditional.

          Role-gated for the same reason the list's "Open a record" is: every row it confirms goes
          through `POST /projects`, which answers a viewer with "a viewer cannot create a record".
          A link that could only ever produce a 403 is an invitation to one.
        */}
        {canCreateProject(claims) && (
          <button
            type="button"
            className={`app__navlink${isCurrent('/projects/import') ? ' app__navlink--on' : ''}`}
            {...(isCurrent('/projects/import') ? { 'aria-current': 'page' as const } : {})}
            onClick={() => void navigate('/projects/import')}
          >
            Import log
          </button>
        )}
        <button
          type="button"
          className={`app__navlink${isCurrent('/standards') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/standards') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/standards')}
        >
          Scoring standard
        </button>
        {/*
          THE PLATFORM-HEALTH PANEL (ADR-0064 D3) — and it is HERE, unconditionally, for reasons that
          differ from the import link above.

          🔴 A PAGE WITH NO DOOR IS NOT SHIPPED. `/admin/members` went out with a route and no link and
          was reachable only by typing the URL; this header carries that note on the analyzer side.
          This panel was one review away from repeating it exactly.

          ⚠️ NOT role-gated, unlike `Import log`. That link is hidden from a viewer because every row it
          confirms goes through `POST /projects`, which answers a viewer with a 403 — a link that could
          only ever produce one is an invitation to one. The same reasoning does NOT reach here: the
          gate is the `can_see_platform_health` CAPABILITY, which is deliberately absent from the
          session claims (it is read per request so a revocation takes effect immediately, not at next
          login). There is nothing in the claims to gate on, and putting it there to hide a link would
          trade a correct security property for a cosmetic one.

          So the Hub — staff-only by origin — shows the link, and a staff member without a grant gets
          the page's own "does not have platform-health access" notice. That is a worse click than a
          hidden link and a better one than an unreachable page.
        */}
        <button
          type="button"
          className={`app__navlink${isCurrent('/admin/platform-health') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/admin/platform-health') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/admin/platform-health')}
        >
          Platform health
        </button>

        {label !== null && <span className="app__email">{label}</span>}

        <button type="button" className="app__signout" onClick={() => void onSignOut()}>
          Sign out
        </button>
      </div>
    </header>
  );
}
